import { accessToken } from "./auth";
import { env } from "./env";

/**
 * Erreur API typée, conforme RFC 7807 quand le serveur la renvoie.
 * `requestId` permet de corréler une erreur UI avec les logs Pino backend.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body: unknown = null,
    public requestId: string | null = null
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type RequestOptions = Omit<RequestInit, "headers"> & { headers?: HeadersInit };

function newRequestId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ?? `web-${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
}

/**
 * Renvoie les headers (Authorization + Accept) prêts à injecter dans un fetch
 * direct quand `api()` n'est pas applicable (upload multipart par ex.).
 */
export async function withAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Request-Id": newRequestId(),
  };
  const token = accessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function isPdfMagic(bytes: Uint8Array): boolean {
  return bytes.byteLength >= 5 && new TextDecoder().decode(bytes.subarray(0, 5)) === "%PDF-";
}

function triggerDownload(url: string, filename: string): void {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/** Télécharge un document protégé (PDF, etc.) avec le Bearer, sans dépendre d'une popup. */
export async function openAuthenticatedDocument(path: string, filename: string): Promise<void> {
  const headers = await withAuthHeaders();
  headers.Accept = "application/pdf,application/octet-stream;q=0.9,*/*;q=0.8";
  const response = await fetch(`${env.VITE_API_URL}${path}`, {
    headers,
    redirect: "follow",
  });
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  if (!response.ok) {
    const text = new TextDecoder().decode(bytes).slice(0, 400);
    let detail = text || response.statusText;
    try {
      const parsed = JSON.parse(text) as { detail?: string };
      if (parsed.detail) detail = parsed.detail;
    } catch {
      /* keep text */
    }
    throw new ApiError(response.status, detail || `Téléchargement impossible (${response.status})`);
  }
  if (!isPdfMagic(bytes)) {
    throw new ApiError(
      response.status,
      "Le serveur n'a pas renvoyé un PDF valide. Réessayez ou régénérez le document."
    );
  }
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, filename);
  const opened = window.open(url, "_blank", "noopener");
  window.setTimeout(() => URL.revokeObjectURL(url), opened ? 60_000 : 2_000);
}

/**
 * Wrapper `fetch` avec injection automatique du Bearer JWT de l'API Fly,
 * correlation ID et parse JSON. Lève `ApiError` si !response.ok.
 */
export async function api<T>(path: string, init: RequestOptions = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const token = accessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Accept", "application/json");
  if (!headers.has("X-Request-Id")) {
    headers.set("X-Request-Id", newRequestId());
  }

  const response = await fetch(`${env.VITE_API_URL}${path}`, { ...init, headers });

  const contentType = response.headers.get("content-type") ?? "";
  const isJson =
    contentType.includes("application/json") || contentType.includes("application/problem+json");
  const body: unknown = isJson ? await response.json().catch(() => null) : await response.text();
  const requestId = response.headers.get("X-Request-Id") ?? headers.get("X-Request-Id");

  if (!response.ok) {
    const message =
      isJson && body && typeof body === "object" && "detail" in body
        ? String((body as { detail: unknown }).detail)
        : response.statusText;
    throw new ApiError(response.status, message, body, requestId);
  }

  return body as T;
}
